import { queryNoTracking } from '../../../services/DataService';

const safeString = (value) => (value ?? '').toString();

const q_ProductCatalog = queryNoTracking('Workflow_QueryShaping_ProductCatalog', 0, true);

/**
 * Template catalog group name for tenant products. Group items carry the product's
 * display name as the label and its stable ProductID guid as the token/snippet:
 * names/codes on line entities are denormalized snapshots that go stale on rename,
 * so only the guid filters reliably. Editors map the raw guid to their literal form
 * via mapCatalogGroup (bare for OData, quoted for JavaScript, name+guid for prompts).
 */
export const PRODUCTS_TEMPLATE_GROUP = 'Products';

/**
 * Fetches the tenant product vocabulary (distinct by ProductID, latest version's
 * name, ordered by name) as a template catalog group, or null when the tenant has
 * no products or the lookup fails — callers just omit the group.
 */
export const fetchProductCatalogGroup = async () => {
  try {
    const res = await q_ProductCatalog.getFirstOrDefault({});
    const products = (Array.isArray(res?.Products) ? res.Products : [])
      .filter(p => safeString(p?.Name).trim() && safeString(p?.ProductID).trim());
    if (products.length === 0) return null;

    return {
      group: PRODUCTS_TEMPLATE_GROUP,
      items: products.map(p => {
        const guid = safeString(p.ProductID).trim().toLowerCase();
        return {
          label: safeString(p.Name).trim(),
          token: guid,
          snippet: guid,
        };
      }),
    };
  } catch {
    return null;
  }
};

/**
 * Prompt-editor mapper for product items: inserts "Name (ProductID guid)" so the
 * request stays human-readable while still carrying the exact guid for the AI.
 * Strips quotes defensively in case the value was already mapped to a literal.
 */
export const toPromptProductReference = (value, item) => {
  const guid = safeString(value).trim().replace(/^["']|["']$/g, '');
  const name = safeString(item?.label).trim();
  return name ? `${name} (ProductID ${guid})` : `ProductID ${guid}`;
};
